/**
 * testing-map loader — parses `*.toml` files under `src/game/maps/testing/`
 * into `GridLevel`-compatible objects and returns them so `LevelRegistry`
 * can register each at boot.
 *
 * Format contract: see [`README.md`](./README.md). Accepts 5 primitives
 * (`[player_start]`, `[[entity]]`, `[[platform]]`, `[[label]]`,
 * `[[vqa_hint]]`) + required `testing_map_schema_version = 1`.
 *
 * Discovery: uses Vite's `import.meta.glob` so a new `<purpose>/<name>.toml`
 * is picked up without a code change.
 *
 * ID convention: file `src/game/maps/testing/<purpose>/<name>.toml` is
 * registered as `testing-<name>` in `LevelRegistry`. `<purpose>` is reserved
 * for future cross-referencing but doesn't appear in the ID.
 */

import { parse as parseToml } from 'smol-toml';
import type { PlatformerLevel } from '../../../shared/CharacterTypes';
import type { GridLevel } from '../../../shared/TileEditorTypes';
import { LayerType } from '../../../shared/TileEditorTypes';
import { gameLogger } from '../../../utils/GameLogger';

export interface TestingMapLabel {
  text: string;
  x: number;
  y: number;
  color?: string;
}

export interface TestingMapVqaHint {
  default_target_id?: string;
  default_target_x?: number;
  default_target_y?: number;
  expected_category?: string;
}

/**
 * Testing-map-shaped extension of `GridLevel`. The `vqaLabels` array is the
 * only field consumed outside the standard map-system path — rendered by
 * `MapRenderer.renderVqaLabels()` when labels are enabled.
 */
export type TestingMap = GridLevel & Partial<PlatformerLevel> & {
  vqaLabels?: TestingMapLabel[];
  vqaHints?: TestingMapVqaHint[];
};

// ─── TOML shape ────────────────────────────────────────────────────

interface TomlPlayerStart { x: number; y: number; }
interface TomlEntity { type: string; id: string; x: number; y: number; [k: string]: unknown; }
interface TomlPlatform {
  id: string; x: number; y: number; width: number; height: number;
  color?: string; isOneWay?: boolean; type?: string;
}

interface TomlTestingMap {
  testing_map_schema_version: number;
  id: string;
  name?: string;
  purpose?: string;
  background_color?: string;
  player_start: TomlPlayerStart;
  entity?: TomlEntity[];
  platform?: TomlPlatform[];
  label?: TestingMapLabel[];
  vqa_hint?: TestingMapVqaHint[];
}

// ─── Constants ─────────────────────────────────────────────────────

const SUPPORTED_SCHEMA_VERSIONS = [1];
const DEFAULT_TILE_SIZE = 32;
const DEFAULT_WIDTH_TILES = 60;   // 60*32 = 1920 px — wide enough for
const DEFAULT_HEIGHT_TILES = 30;  // 30*32 =  960 px — projectile lanes
const DEFAULT_GRAVITY = 1000;

// ─── Conversion ────────────────────────────────────────────────────

/**
 * Convert one parsed TOML object into a registrable testing map.
 * Validates schema version; throws if unsupported (loader callers
 * catch + warn so one bad file doesn't break boot for the rest).
 */
export function tomlToTestingMap(raw: TomlTestingMap, sourcePath: string): TestingMap {
  if (!SUPPORTED_SCHEMA_VERSIONS.includes(raw.testing_map_schema_version)) {
    throw new Error(
      `[testing-map] ${sourcePath}: unsupported ` +
      `testing_map_schema_version=${raw.testing_map_schema_version}. ` +
      `Supported: ${SUPPORTED_SCHEMA_VERSIONS.join(', ')}.`,
    );
  }
  if (!raw.id || !raw.player_start) {
    throw new Error(
      `[testing-map] ${sourcePath}: missing required 'id' or 'player_start'.`,
    );
  }

  const mapId = `testing-${raw.id}`;
  const T = DEFAULT_TILE_SIZE;
  const W = DEFAULT_WIDTH_TILES;
  const H = DEFAULT_HEIGHT_TILES;

  const entities = (raw.entity ?? []).map((e) => ({
    type: e.type,
    id: e.id,
    x: e.x,
    y: e.y,
  }));

  const platforms = (raw.platform ?? []).map((p) => ({
    id: p.id,
    x: p.x,
    y: p.y,
    width: p.width,
    height: p.height,
    type: (p.type ?? 'normal') as 'normal',
    color: p.color ?? '#2d3748',
    ...(p.isOneWay ? { isOneWay: true } : {}),
  }));

  const spawnPoints = [{ x: raw.player_start.x / T, y: raw.player_start.y / T }];
  const defaultSpawn = { x: raw.player_start.x, y: raw.player_start.y };

  const emptyTiles = () => new Map<string, number>();

  return {
    id: mapId,
    name: raw.name ?? raw.id,
    version: '1.0',
    widthTiles: W,
    heightTiles: H,
    width: W * T,
    height: H * T,
    tileSize: T,
    backgroundColor: raw.background_color ?? '#1a1a2e',
    gravity: DEFAULT_GRAVITY,
    spawnPoints,
    defaultSpawn,
    layers: [
      { name: LayerType.GROUND,    visible: true, locked: false, opacity: 1.0, tiles: emptyTiles() },
      { name: LayerType.COLLISION, visible: true, locked: false, opacity: 1.0, tiles: emptyTiles() },
      { name: LayerType.OBJECTS,   visible: true, locked: false, opacity: 1.0, tiles: emptyTiles() },
      { name: LayerType.ENTITIES,  visible: true, locked: false, opacity: 1.0, tiles: emptyTiles(), entities },
      { name: LayerType.TRIGGERS,  visible: true, locked: false, opacity: 1.0, tiles: emptyTiles() },
    ],
    platforms,
    entities,
    spawnRules: [],
    type: 'field' as const,
    metadata: {
      author: 'testing-map loader',
      description: raw.purpose ?? `Testing map ${raw.id}`,
      tags: ['testing', 'vqa'],
    },
    vqaLabels: raw.label ?? [],
    vqaHints: raw.vqa_hint ?? [],
  } as TestingMap;
}

// ─── Boot discovery ────────────────────────────────────────────────

/**
 * Eagerly import every `.toml` under `src/game/maps/testing/**` as a raw
 * string, parse, convert to TestingMap. Each file's error is isolated —
 * one bad file warns loud but doesn't block the rest from loading.
 *
 * Called once by `LevelRegistry.registerDefaultLevels()` at boot.
 */
export function loadTestingMaps(): TestingMap[] {
  // Vite converts this glob to a synchronous import map at build time.
  // Outside Vite (jest/ts-node), import.meta is unavailable — return empty
  // and let callers set up testing maps via tomlToTestingMap() instead.
  let modules: Record<string, string>;
  try {
    // @ts-ignore -- TS1343: import.meta only valid in ESM; ts-jest runs CJS
    modules = (import.meta as any).glob('./**/*.toml', {
      query: '?raw',
      import: 'default',
      eager: true,
    }) as Record<string, string>;
  } catch {
    return [];
  }

  const out: TestingMap[] = [];
  for (const [sourcePath, tomlStr] of Object.entries(modules)) {
    try {
      const raw = parseToml(tomlStr) as unknown as TomlTestingMap;
      out.push(tomlToTestingMap(raw, sourcePath));
    } catch (err) {
      gameLogger.warn(
        'map',
        `[testing-map] failed to load ${sourcePath}: ${
          err instanceof Error ? err.message : String(err)
        }`,
      );
    }
  }
  return out;
}
